import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox as MUICheckbox,
  Divider,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  MenuItem,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import capitalize from 'lodash/capitalize';
import React from 'react';

import { IColumnItem } from '../../../types/tables';

interface IProps {
  anchorEl: HTMLElement | null;
  position: { top: number; left: number } | null;
  open: boolean;
  onClose: () => void;
  columns: IColumnItem[];
  visibleColumns: Record<string, boolean>;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onColumnVisibilityChange: (columnId: string) => void;
  onToggleAll: (checked: boolean) => void;
  onReset: () => void;
  requiredColumnIds: string[];
  areAllColumnsVisible: boolean;
}

const popoverContainer = {
  px: 0,
  py: 1.5,
  minWidth: 250,
};

const listContainer = {
  maxHeight: 400,
  overflowY: 'auto',
  mb: 2,
};

const formGroup = {
  display: 'flex',
  flexDirection: 'column',
};

const menuItem = {
  p: 0,
  '& .MuiFormControlLabel-root': {
    width: '100%',
    marginRight: 0,
    paddingLeft: 0.5,
  },
};

const footerContainer = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  mt: 1,
  px: 2,
};

const footerCheckbox = {
  px: 0,
  py: 0.5,
  mr: 2,
};

const resetButtonSx = {
  fontSize: 14,
  marginLeft: 2,
};

const ColumnsVisibilityPopover: React.FC<IProps> = ({
  anchorEl,
  position,
  open,
  onClose,
  columns,
  visibleColumns,
  searchQuery,
  onSearchChange,
  onColumnVisibilityChange,
  onToggleAll,
  onReset,
  requiredColumnIds,
  areAllColumnsVisible,
}) => {
  const filteredColumns = React.useMemo(() => {
    if (!searchQuery) {
      return columns;
    }

    return columns.filter((column) => {
      const label = typeof column.label === 'string' ? column.label : column.id;
      return label.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [columns, searchQuery]);

  const isIndeterminate = !areAllColumnsVisible && Object.values(visibleColumns).some((v) => v);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      keepMounted
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      anchorPosition={position || undefined}
      anchorReference={position ? 'anchorPosition' : 'anchorEl'}
    >
      <Box sx={popoverContainer}>
        <Typography variant="subtitle2" sx={{ mb: 1, px: 1.5 }}>
          Select visible columns
        </Typography>
        <Box sx={{ px: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{ mb: 2 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
              htmlInput: {
                'data-action': 'columns_visibility_search',
              },
            }}
          />
        </Box>
        <Box sx={listContainer}>
          <FormGroup sx={formGroup}>
            {filteredColumns.map(({ id, label }) => {
              const displayLabel = id === 'sku' ? 'SKU' : capitalize(typeof label === 'string' ? label : id);

              return (
                <MenuItem sx={menuItem} key={id} selected={visibleColumns[id]} data-action={`column_toggle_${id}`}>
                  <FormControlLabel
                    key={id}
                    sx={{ ml: 0 }}
                    control={
                      <MUICheckbox
                        sx={{ px: 1.5, py: 0.5, mr: 0.5 }}
                        checked={visibleColumns[id]}
                        onChange={() => onColumnVisibilityChange(id)}
                        disabled={requiredColumnIds.includes(id)}
                        slotProps={{
                          input: {
                            'data-action': `column_checkbox_${id}`,
                          } as React.InputHTMLAttributes<HTMLInputElement>,
                        }}
                      />
                    }
                    label={displayLabel}
                  />
                </MenuItem>
              );
            })}
          </FormGroup>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={footerContainer}>
          <FormControlLabel
            sx={{ ml: 0 }}
            control={
              <MUICheckbox
                sx={footerCheckbox}
                checked={areAllColumnsVisible}
                indeterminate={isIndeterminate}
                onChange={(e) => onToggleAll(e.target.checked)}
                slotProps={{
                  input: {
                    'data-action': 'columns_toggle_all',
                  } as React.InputHTMLAttributes<HTMLInputElement>,
                }}
              />
            }
            label="Show/Hide all"
          />

          <Button size="small" onClick={onReset} sx={resetButtonSx} data-action="columns_reset">
            Reset
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default ColumnsVisibilityPopover;
